/**
 * Write a quote for a lead (contractor pages phase 3): the amount, what the
 * job covers, optional line items, terms and how long it stays open, with the
 * success-fee calculator always on screen so the contractor prices with the
 * fee in view rather than finding it on the statement.
 *
 * Three ways in: a new quote (POST), a revision of a declined / expired /
 * withdrawn one (POST, prefilled), or a draft being edited (PATCH, then the
 * separate send). Sending always passes through a confirm that restates the
 * fee.
 */
import { useId, useState } from 'react';
import type { ApiError, FieldIssue } from '../api/errors';
import type { LeadQuoteSummary, QuoteBlockedReason, QuoteConflictCode, QuoteCreateBody, QuoteLineItemInput } from '../api/types';
import { useSession } from '../auth/session';
import { ErrorNotice, Field, Modal } from '../ui/kit';
import { useMutation } from '../ui/hooks';
import {
  centsToDollarInput,
  formatBps,
  formatCents,
  grossForNet,
  lineItemTotalCents,
  lineTotalCents,
  netCents,
  parseDollarsToCents,
  parseQuantity,
  successFeeCents,
} from './feeMath';

export const SCOPE_MAX = 4000;
export const TERMS_MAX = 2000;
export const LINES_MAX = 40;
/** The API's line description limit is 200; the composer keeps room for " (2.5 × $120.00)". */
export const LINE_DESCRIPTION_MAX = 160;
/** The `lead_quotes.amount_cents` ceiling ($1,000,000). */
export const AMOUNT_MAX_CENTS = 100_000_000;
export const DEFAULT_VALID_DAYS = 14;

/**
 * Why a quote cannot be sent, in plain English. The same codes come back as
 * `blocked_reason` on the list and as the 409 `code` on a create or send.
 */
export const BLOCKED_COPY: Record<QuoteBlockedReason, string> = {
  QUOTES_DISABLED:
    'Quotes aren’t switched on for your organisation yet. FinderlyFix turns them on together with contractor billing, and nothing is charged until then.',
  BILLING_NOT_ENABLED:
    'Quotes go out once contractor billing is set up for your organisation. FinderlyFix will let you know when it’s ready; nothing is charged until then.',
  FEE_SCHEDULE_REQUIRED: 'Accept the Contractor Fee Schedule before you send a quote.',
  LEAD_NOT_ACCEPTED: 'Accept the lead first. Once it’s accepted you can send the homeowner a quote.',
  CONTACT_EMAIL_MISSING: 'This homeowner didn’t leave an email, so a quote can’t be sent. Call them to agree the price instead.',
  QUOTE_OPEN_EXISTS: 'This lead already has an open quote. Withdraw it before you send a new one.',
  PROJECT_EXISTS: 'This homeowner already accepted a quote; the job is below.',
};

export function isBlockedReason(code: string | null | undefined): code is QuoteBlockedReason {
  return !!code && Object.prototype.hasOwnProperty.call(BLOCKED_COPY, code);
}

const MOVED_ON = 'This job has moved on since the page loaded. Reload to see where it stands.';

/** The other refusals a quote or project write can meet, in plain English. */
export const CONFLICT_COPY: Record<QuoteConflictCode, string> = {
  QUOTE_NOT_DRAFT: 'This quote was already sent, so it can no longer be edited. Reload to see it.',
  QUOTE_NOT_OPEN: 'This quote is no longer open; the homeowner may have just answered it. Reload to see where it stands.',
  LINE_ITEMS_MISMATCH: 'The line items must add up to the price of the job.',
  PROJECT_NOT_STARTABLE: MOVED_ON,
  PROJECT_NOT_COMPLETABLE: MOVED_ON,
  PROJECT_NOT_CANCELLABLE: 'This job can no longer be cancelled: completion was already reported. Contact FinderlyFix if something went wrong.',
};

/** A quote / project write's error as one sentence, when the code has one. */
export function quoteErrorText(error: ApiError | null): string | null {
  if (!error) return null;
  if (isBlockedReason(error.code)) return BLOCKED_COPY[error.code];
  return Object.prototype.hasOwnProperty.call(CONFLICT_COPY, error.code) ? CONFLICT_COPY[error.code as QuoteConflictCode] : null;
}

/** `quoteErrorText` when there is one, else the generic ErrorNotice. */
export function QuoteError({ error }: { error: ApiError | null }) {
  const text = quoteErrorText(error);
  if (!error) return null;
  return text ? (
    <div className="notice bad" role="alert">
      {text}
    </div>
  ) : (
    <ErrorNotice error={error} />
  );
}

/** "Labour" × 2.5 at $120 → "Labour (2.5 × $120)"; a quantity of 1 keeps the description as typed. */
export function lineDescription(description: string, qty: number, unitCents: number): string {
  const d = description.trim();
  return qty === 1 ? d : `${d} (${qty} × ${formatCents(unitCents)})`;
}

const QTY_SUFFIX = /^(.*\S) \((\d+(?:\.\d{1,2})?) × (\$[\d,]+(?:\.\d{2})?)\)$/;

/** The reverse of `lineDescription`, when the stored amount still matches. */
export function splitLineDescription(description: string, amountCents: number): { description: string; qty: number; unitCents: number } {
  const m = QTY_SUFFIX.exec(description);
  if (m) {
    const qty = Number(m[2]);
    const unit = parseDollarsToCents(m[3] ?? '');
    if (unit !== null && qty > 0 && lineTotalCents(qty, unit) === amountCents) return { description: m[1] ?? description, qty, unitCents: unit };
  }
  return { description, qty: 1, unitCents: amountCents };
}

interface LineRow {
  key: number;
  description: string;
  qty: string;
  unit: string;
}

let nextRowKey = 1;
const newRow = (over: Partial<LineRow> = {}): LineRow => ({ key: nextRowKey++, description: '', qty: '1', unit: '', ...over });

function rowsFrom(quote: LeadQuoteSummary | null | undefined): LineRow[] {
  return (quote?.line_items ?? []).map((item) => {
    const split = splitLineDescription(item.description, lineItemTotalCents(item));
    return newRow({ description: split.description, qty: String(split.qty), unit: centsToDollarInput(split.unitCents) });
  });
}

/** Days between a stored quote's creation and `valid_until`, clamped to the API's 1–90. */
function validDaysFrom(quote: LeadQuoteSummary | null | undefined): number {
  if (!quote?.valid_until) return DEFAULT_VALID_DAYS;
  const from = new Date(quote.sent_at ?? quote.created_at).getTime();
  const to = new Date(quote.valid_until).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to)) return DEFAULT_VALID_DAYS;
  return Math.min(90, Math.max(1, Math.round((to - from) / 86_400_000)));
}

export interface QuoteComposerProps {
  leadId: string;
  /** The success fee in basis points a quote sent now snapshots (3000 = 30%). */
  feeBps: number;
  /** A draft to edit in place (PATCH). */
  draft?: LeadQuoteSummary | null;
  /** An earlier quote to start a revision from (a new POST, prefilled). */
  prefill?: LeadQuoteSummary | null;
  /** After a save or send; `sent` says which. */
  onDone: (quote: LeadQuoteSummary, sent: boolean) => void;
  onCancel: () => void;
}

export function QuoteComposer({ leadId, feeBps, draft, prefill, onDone, onCancel }: QuoteComposerProps) {
  const { api } = useSession();
  const source = draft ?? prefill ?? null;
  const headingId = useId();
  const [amountText, setAmountText] = useState(() => centsToDollarInput(source?.amount_cents ?? 0));
  const [scope, setScope] = useState(source?.scope ?? '');
  const [terms, setTerms] = useState(source?.terms ?? '');
  const [validDays, setValidDays] = useState(() => String(validDaysFrom(source)));
  const [rows, setRows] = useState<LineRow[]>(() => rowsFrom(source));
  const [takeHome, setTakeHome] = useState('');
  const [tried, setTried] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const pct = formatBps(feeBps);
  const hasLines = rows.length > 0;
  const lines = rows.map((r) => {
    const qty = parseQuantity(r.qty);
    const unit = parseDollarsToCents(r.unit);
    const complete = r.description.trim() !== '' && qty !== null && unit !== null && unit > 0;
    return { row: r, qty, unit, complete, total: qty !== null && unit !== null ? lineTotalCents(qty, unit) : 0 };
  });
  const linesSum = lines.reduce((sum, l) => sum + l.total, 0);
  const typedAmount = parseDollarsToCents(amountText);
  const amountCents = hasLines ? linesSum : (typedAmount ?? 0);
  const fee = successFeeCents(amountCents, feeBps);
  const net = netCents(amountCents, feeBps);
  const days = Number(validDays);

  // ── Validation (shown after the first Save / Send) ────────────────────
  const issues: Record<'amount' | 'scope' | 'terms' | 'days' | 'lines', FieldIssue[]> = {
    amount: [],
    scope: [],
    terms: [],
    days: [],
    lines: [],
  };
  const issue = (key: keyof typeof issues, message: string) => issues[key].push({ path: key, message });
  if (hasLines) {
    if (lines.some((l) => !l.complete)) issue('lines', 'Give every line a description, a quantity and a unit price, or remove it.');
    else if (linesSum <= 0) issue('lines', 'The line items add up to nothing.');
  } else if (amountText.trim() === '') issue('amount', 'Enter the price of the job.');
  else if (typedAmount === null) issue('amount', 'Enter the price in dollars, like 1250 or 1,250.50.');
  else if (typedAmount <= 0) issue('amount', 'The price must be more than $0.');
  if (amountCents > AMOUNT_MAX_CENTS) issue(hasLines ? 'lines' : 'amount', `A quote can be at most ${formatCents(AMOUNT_MAX_CENTS)}.`);
  if (!scope.trim()) issue('scope', 'Say what the job covers, so the homeowner knows what they’re accepting.');
  if (scope.length > SCOPE_MAX) issue('scope', `Keep it to ${SCOPE_MAX} characters.`);
  if (terms.length > TERMS_MAX) issue('terms', `Keep it to ${TERMS_MAX} characters.`);
  if (!Number.isInteger(days) || days < 1 || days > 90) issue('days', 'Between 1 and 90 days.');
  const valid = Object.values(issues).every((list) => list.length === 0);
  const shown = (key: keyof typeof issues) => (tried ? issues[key] : []);

  function body(): QuoteCreateBody {
    const out: QuoteCreateBody = { amount_cents: amountCents, scope: scope.trim(), valid_days: days };
    if (hasLines) {
      out.line_items = lines.map<QuoteLineItemInput>((l) => ({
        description: lineDescription(l.row.description, l.qty ?? 1, l.unit ?? 0),
        amount_cents: l.total,
      }));
    } else if (draft?.line_items?.length) {
      // Editing a draft whose lines were all removed: clear them (the API refuses []).
      out.line_items = null;
    }
    if (terms.trim()) out.terms = terms.trim();
    else if (draft?.terms) out.terms = null;
    return out;
  }

  const save = useMutation(async (send: boolean) => {
    const b = body();
    let quote: LeadQuoteSummary;
    if (draft) {
      quote = (await api.updateQuote(draft.id, b)).quote;
      if (send) quote = (await api.sendQuote(draft.id)).quote;
    } else {
      quote = (await api.createQuote(leadId, send ? { ...b, send: true } : b)).quote;
    }
    onDone(quote, send);
    return quote;
  });

  function attempt(send: boolean) {
    setTried(true);
    if (!valid) return;
    if (send) setConfirming(true);
    else void save.run(false);
  }

  const title = draft ? `Edit draft quote (v${draft.version})` : prefill ? 'Send a revised quote' : 'Create a quote';

  return (
    <form
      className="stack"
      aria-labelledby={headingId}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        attempt(true);
      }}
    >
      <h3 id={headingId} style={{ margin: 0 }}>
        {title}
      </h3>

      <Field
        label="Price of the job (USD)"
        issues={shown('amount')}
        hint={hasLines ? 'Set by the line items below.' : 'What the homeowner pays you, before any FinderlyFix fee.'}
      >
        {(p) => (
          <input
            {...p}
            className="input"
            inputMode="decimal"
            autoComplete="off"
            value={hasLines ? centsToDollarInput(linesSum) : amountText}
            readOnly={hasLines}
            onChange={(e) => setAmountText(e.target.value)}
          />
        )}
      </Field>

      <FeeCalculator pct={pct} fee={fee} net={net} />

      <div className="sunk stack-sm">
        <strong>Price your quote</strong>
        <Field
          label="I want to take home (USD)"
          optional
          hint={
            hasLines
              ? 'Remove the line items to price from what you want to keep.'
              : `Fills the price for you. To take home N, quote N ÷ ${(1 - feeBps / 10_000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}.`
          }
        >
          {(p) => (
            <input
              {...p}
              className="input"
              inputMode="decimal"
              autoComplete="off"
              value={takeHome}
              disabled={hasLines}
              onChange={(e) => {
                setTakeHome(e.target.value);
                const target = parseDollarsToCents(e.target.value);
                if (target !== null && target > 0) {
                  const gross = grossForNet(target, feeBps);
                  if (Number.isFinite(gross)) setAmountText(centsToDollarInput(gross));
                }
              }}
            />
          )}
        </Field>
      </div>

      <Field label="What the job covers" issues={shown('scope')} hint={`${scope.length} / ${SCOPE_MAX}`}>
        {(p) => <textarea {...p} className="input" rows={5} maxLength={SCOPE_MAX} value={scope} onChange={(e) => setScope(e.target.value)} />}
      </Field>

      <fieldset className="stack-sm" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="label" style={{ padding: 0 }}>
          Line items <span className="faint" style={{ fontWeight: 500 }}>· optional</span>
        </legend>
        {rows.length ? (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Description</th>
                  <th scope="col">Qty</th>
                  <th scope="col">Unit price</th>
                  <th scope="col" className="num">
                    Total
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.row.key}>
                    <td>
                      <input
                        className="input input-sm"
                        aria-label={`Line ${i + 1} description`}
                        maxLength={LINE_DESCRIPTION_MAX}
                        value={l.row.description}
                        onChange={(e) => setRows(rows.map((r) => (r.key === l.row.key ? { ...r, description: e.target.value } : r)))}
                      />
                    </td>
                    <td>
                      <input
                        className="input input-sm"
                        aria-label={`Line ${i + 1} quantity`}
                        inputMode="decimal"
                        style={{ width: 64 }}
                        value={l.row.qty}
                        onChange={(e) => setRows(rows.map((r) => (r.key === l.row.key ? { ...r, qty: e.target.value } : r)))}
                      />
                    </td>
                    <td>
                      <input
                        className="input input-sm"
                        aria-label={`Line ${i + 1} unit price (USD)`}
                        inputMode="decimal"
                        style={{ width: 104 }}
                        value={l.row.unit}
                        onChange={(e) => setRows(rows.map((r) => (r.key === l.row.key ? { ...r, unit: e.target.value } : r)))}
                      />
                    </td>
                    <td className="num">{formatCents(l.total)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        aria-label={`Remove line ${i + 1}`}
                        onClick={() => setRows(rows.filter((r) => r.key !== l.row.key))}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {shown('lines').length ? (
          <div className="err" role="alert">
            <span aria-hidden="true">●</span>
            <span>{shown('lines').map((x) => x.message).join(' ')}</span>
          </div>
        ) : null}
        <div className="row">
          <button
            type="button"
            className="btn btn-sm"
            disabled={rows.length >= LINES_MAX}
            onClick={() => setRows([...rows, newRow(rows.length === 0 && typedAmount ? { unit: centsToDollarInput(typedAmount) } : {})])}
          >
            Add a line item
          </button>
          {hasLines ? <span className="small muted">Lines total {formatCents(linesSum)}; that is the price of the quote.</span> : null}
        </div>
      </fieldset>

      <Field label="Terms" optional issues={shown('terms')} hint={`Payment, warranty, what’s not included. ${terms.length} / ${TERMS_MAX}`}>
        {(p) => <textarea {...p} className="input" rows={3} maxLength={TERMS_MAX} value={terms} onChange={(e) => setTerms(e.target.value)} />}
      </Field>

      <Field label="Valid for (days)" issues={shown('days')} hint="After this the homeowner can no longer accept it. 1 to 90 days.">
        {(p) => (
          <input
            {...p}
            className="input"
            type="number"
            min={1}
            max={90}
            inputMode="numeric"
            style={{ maxWidth: 120 }}
            value={validDays}
            onChange={(e) => setValidDays(e.target.value)}
          />
        )}
      </Field>

      <QuoteError error={save.error} />

      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={save.pending}>
          Send to the homeowner…
        </button>
        <button type="button" className="btn" disabled={save.pending} onClick={() => attempt(false)}>
          {save.pending && !confirming ? 'Saving…' : 'Save draft'}
        </button>
        <button type="button" className="btn btn-ghost" disabled={save.pending} onClick={onCancel}>
          Cancel
        </button>
      </div>

      <SendQuoteDialog
        open={confirming}
        amount={amountCents}
        feeBps={feeBps}
        pending={save.pending}
        onClose={() => setConfirming(false)}
        onConfirm={async () => {
          await save.run(true);
          setConfirming(false);
        }}
      />
    </form>
  );
}

/** "FinderlyFix success fee (30%): $X · You keep: $Y", live. */
export function FeeCalculator({ pct, fee, net }: { pct: string; fee: number; net: number }) {
  return (
    <div className="notice info" aria-live="polite" data-testid="fee-calculator">
      <div className="stack-sm" style={{ gap: 2 }}>
        <span>
          <strong>
            FinderlyFix success fee ({pct}): {formatCents(fee)}
          </strong>{' '}
          · You keep: <strong>{formatCents(net)}</strong>
        </span>
        <span className="small muted">
          Charged only when the homeowner accepts and the job is confirmed complete.
        </span>
      </div>
    </div>
  );
}

/** The confirm before a quote goes out: restates the price and the fee. */
export function SendQuoteDialog({
  open,
  amount,
  feeBps,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  amount: number;
  feeBps: number;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const fee = successFeeCents(amount, feeBps);
  return (
    <Modal
      open={open}
      title="Send this quote to the homeowner?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" disabled={pending} onClick={onClose}>
            Keep editing
          </button>
          <button type="button" className="btn btn-primary" disabled={pending} onClick={() => void onConfirm()}>
            {pending ? 'Sending…' : `Send quote for ${formatCents(amount)}`}
          </button>
        </>
      }
    >
      <p style={{ margin: 0 }}>
        The homeowner gets an email with your quote for <strong>{formatCents(amount)}</strong> and can accept or decline it online.
      </p>
      <p style={{ margin: 0 }}>
        If they accept and the job is confirmed complete, the FinderlyFix success fee ({formatBps(feeBps)}) of{' '}
        <strong>{formatCents(fee)}</strong> is added to your statement. You keep <strong>{formatCents(netCents(amount, feeBps))}</strong>.
      </p>
    </Modal>
  );
}
